/*
 * Copyright (c) 2019 - Indigen Solutions
 * Authors:
 *   - Bichoy Messiha <bm@indigen.com>
 * NOTICE: All information contained herein is, and remains
 * the property of Indigen Solutions and its suppliers,
 * if any.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Indigen Solutions.
 */

import { chain, filter, find, forEach, isNil, map, sortBy } from "lodash";

export class BaseSearch {
    constructor() {
        this.conventions = [];
        this.conventionsEngine = null;
    }

    async waitConventions() {
        const promise = new Promise(resolve => {
            const interval = setInterval(() => {
                if (this.conventions && this.conventions.length > 0) {
                    clearInterval(interval);
                    resolve(true);
                }
            }, 10);
        });
        return promise;
    }

    async waitConventionsEngineInit() {
        return await this.waitConventions();
    }

    getTotalConventions() {
        return this.conventions.length;
    }

    /**
     * Search results by text
     * @param query
     */
    searchConventionsText(query, territory) {
        if (query.length < 3 || !this.conventionsEngine) return [];
        query = this.clean(query);
        const results = this.conventionsEngine.search(query, {
            fuzzy: 0.05,
            boost: { title: 100 },
        });
        return this.manageResults(results, territory, 0);
    }

    /**
     * Search results by Brochure
     * @param query
     */
    searchConventionsBrochures(query, territory) {
        if (!this.conventionsEngine) return [];
        let results = this.conventionsEngine.search(query.toLowerCase(), {
            fields: ["brochure"],
            fuzzy: 0,
            prefix: false,
        });
        return this.manageResults(results, territory, 0);
    }

    /**
     * Search results by IDCC
     * @param query
     */
    searchConventionsIdcc(query, territory) {
        if (!this.conventionsEngine) return [];
        let results = this.conventionsEngine.search(query.toLowerCase(), {
            fields: ["idcc"],
            fuzzy: 0,
            prefix: false,
        });
        return this.manageResults(results, territory, 0);
    }

    /**
     *
     * @param query
     */
    getSuggestionsBrochures(query) {
        if (!this.conventionsEngine) return;
        const results = this.conventionsEngine.search(query, { fields: ["brochure"] });
        const resultsUnique = this.uniqueBySuggestion(results);
        return map(resultsUnique, r => ({
            suggestion: r.brochure,
            type: "code",
            subtype: "brochure",
            label: r.title,
            score: r.score,
        }));
    }

    /**
     *
     * @param query
     */
    getSuggestionsIdcc(query) {
        if (!this.conventionsEngine) return;
        const results = this.conventionsEngine.search(query, { fields: ["idcc"] });
        const resultsUnique = this.uniqueBySuggestion(results);
        return map(resultsUnique, r => ({
            suggestion: r.idcc,
            type: "code",
            subtype: "idcc",
            label: r.title,
            score: r.score,
        }));
    }

    /**
     *
     * @param query
     */
    getSuggestionsBrochuresWithShortTitlePanorama(query) {
        if (!this.conventionsEngine) return;
        const results = this.conventionsEngine
            .search(query, {
                fields: ["brochure", "shortTitle", "isDenounced", "type"],
            })
            .filter(
                ccn => !ccn.isDenounced && (ccn.type === "syndiquee") | (ccn.type === "etendue")
            );
        return map(results, r => ({
            suggestion: r.brochure,
            type: "code",
            subtype: "brochure",
            label: r.shortTitle,
            score: r.score,
        }));
    }

    /**
     *
     * @param query
     */
    getSuggestionsIdccWithShortTitlePanorama(query) {
        if (!this.conventionsEngine) return;
        const results = this.conventionsEngine
            .search(query, {
                fields: ["idcc", "shortTitle", "isDenounced", "type"],
            })
            .filter(
                ccn => !ccn.isDenounced && (ccn.type === "syndiquee") | (ccn.type === "etendue")
            );
        return map(results, r => ({
            suggestion: r.idcc,
            type: "code",
            subtype: "idcc",
            label: r.shortTitle,
            score: r.score,
        }));
    }

    /**
     *
     * @param query
     */
    uniqueBySuggestion(results) {
        return chain(results)
            .groupBy(result => result.suggestion)
            .mapValues(resultsPerSuggestion => {
                const parentSuggestion = resultsPerSuggestion.find(r =>
                    isNil(r.parentConventionId)
                );
                return parentSuggestion || resultsPerSuggestion[0];
            })
            .values()
            .flatten()
            .value();
    }

    /**
     *
     * @param results
     * @param territory
     */
    filterTerritory(results, territory) {
        return filter(
            results,
            r =>
                r.territoryCodes.indexOf(territory) > -1 ||
                r.territoryCodes.indexOf("met") > -1 ||
                r.territoryCodes.indexOf("all") > -1
        );
    }

    /**
     *
     * @param results
     */
    manageResults(results, territory, scoreLimit) {
        const conventions = this.getConventionsFromResults(results, scoreLimit);
        const nationalConventions = filter(conventions, r => r.territoryRange === "national");
        const localConventions = filter(conventions, r => r.territoryRange === "local");
        return territory
            ? this.filterTerritory(localConventions, territory).concat(nationalConventions)
            : nationalConventions.concat(localConventions);
    }

    /**
     *
     * @param results
     * @param scoreLimit
     */
    getConventionsFromResults(results, scoreLimit) {
        let conventions = {};
        results = sortBy(results, r => r.score).reverse();
        forEach(results, result => {
            if (result.score > scoreLimit) {
                const convention = result.parentConventionId
                    ? this.getParentConvention(result)
                    : this.getConvention(result);
                if (!conventions[convention.conventionId])
                    conventions[convention.conventionId] = convention;
            }
        });
        return sortBy(conventions, r => r.score).reverse();
    }
    getConvention(result) {
        const convention = find(
            this.conventions,
            c => c.conventionId === result.conventionId && c.variantNumber === result.variantNumber
        );
        return {
            ...convention,
            score: result.score,
        };
    }

    getParentConvention(result) {
        const convention = find(
            this.conventions,
            c => c.conventionId === result.parentConventionId
        );
        return {
            ...convention,
            score: result.score,
            selectedChildConvention: this.getConvention(result),
        };
    }

    /**
     *
     * @param string
     */
    clean(string) {
        string = string.replace(/<\/?[^>]+(>|$)/g, "");
        string = string.replace(/(>|<)/g, "");
        string = string.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        string = string.replace("-", "");
        return string.toLowerCase();
    }

    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
